import React, { useCallback, useEffect, useRef, useState } from 'react';
import PlanCard from './PlanCard';

/**
 * A horizontally sliding row of PlanCards.
 *
 * CSS scroll-snap rather than a carousel dependency: the track is a real
 * scroll container, so trackpad and touch swiping work for free, the arrows
 * and dots just call scrollTo, and there is no new package to keep current.
 *
 * Scroll position is the single source of truth for which card is active —
 * deriving it from scrollLeft rather than tracking an index means a swipe and
 * an arrow click can never disagree.
 *
 * Sliding chrome appears only when the track actually OVERFLOWS, not merely
 * when there is more than one plan. Two cards that both fit were showing dots
 * and an arrow that sat on top of the first card, with the second dot lit on
 * load because the nearest-to-centre card in a non-overflowing track is the
 * last one.
 */

const DEFAULT_ACCENT = '#4F46E5';
const CARD_WIDTH = 300;
const GAP = 20;
const ARROW_GUTTER = 40;

const styles = {
    wrap: { position: 'relative' },
    // The gutters keep the arrows clear of the cards instead of overlapping them.
    viewport: (gutter) => ({ padding: `0 ${gutter}px` }),
    track: (overflowing) => ({
        display: 'flex',
        gap: GAP,
        justifyContent: overflowing ? 'flex-start' : 'center',
        overflowX: overflowing ? 'auto' : 'visible',
        scrollSnapType: 'x mandatory',
        scrollBehavior: 'smooth',
        // Room above for the "Active Plan" / "Most Popular" badges, which sit
        // over the card's top edge.
        padding: '14px 2px 6px',
        margin: '0 -2px',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
    }),
    item: {
        flex: `0 0 min(${CARD_WIDTH}px, 100%)`,
        scrollSnapAlign: 'center',
    },
    arrow: (side, shown) => ({
        position: 'absolute',
        top: '50%',
        [side]: 0,
        transform: 'translateY(-50%)',
        width: 32,
        height: 32,
        borderRadius: '50%',
        border: '1px solid #E4E7EC',
        background: '#fff',
        boxShadow: '0 2px 6px rgba(16,24,40,0.10)',
        cursor: 'pointer',
        display: shown ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        zIndex: 2,
    }),
    dots: { display: 'flex', justifyContent: 'center', gap: 6, marginTop: 14 },
    dot: (active, accent) => ({
        width: active ? 18 : 6,
        height: 6,
        borderRadius: 999,
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        background: active ? accent : '#D0D5DD',
        transition: 'width 150ms ease, background 150ms ease',
    }),
};

function Chevron({ dir }) {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
                d={dir === 'left' ? 'M15 18L9 12L15 6' : 'M9 6L15 12L9 18'}
                stroke="#344054" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            />
        </svg>
    );
}

export default function PlanCarousel({
    plans, onSelect, busyPlanId = null, ctaLabel, accent = DEFAULT_ACCENT,
    allowCurrent = false,
}) {
    const trackRef = useRef(null);
    const itemRefs = useRef([]);
    const [active, setActive] = useState(0);
    const [overflowing, setOverflowing] = useState(false);

    const measure = useCallback(() => {
        const track = trackRef.current;
        if (!track) return;
        setOverflowing(track.scrollWidth > track.clientWidth + 1);
    }, []);

    const syncActive = useCallback(() => {
        const track = trackRef.current;
        if (!track) return;
        const centre = track.scrollLeft + track.clientWidth / 2;
        let nearest = 0;
        let best = Infinity;
        itemRefs.current.forEach((el, i) => {
            if (!el) return;
            const elCentre = el.offsetLeft + el.offsetWidth / 2;
            const distance = Math.abs(elCentre - centre);
            if (distance < best) {
                best = distance;
                nearest = i;
            }
        });
        setActive(nearest);
    }, []);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return undefined;
        measure();
        track.addEventListener('scroll', syncActive, { passive: true });
        window.addEventListener('resize', measure);
        return () => {
            track.removeEventListener('scroll', syncActive);
            window.removeEventListener('resize', measure);
        };
    }, [measure, syncActive, plans.length]);

    // Only meaningful once the track scrolls; in a fitting track every card is
    // already visible and "active" would be a lie.
    useEffect(() => {
        if (overflowing) syncActive();
        else setActive(0);
    }, [overflowing, syncActive]);

    const scrollTo = (index) => {
        const clamped = Math.max(0, Math.min(plans.length - 1, index));
        const el = itemRefs.current[clamped];
        const track = trackRef.current;
        if (!el || !track) return;
        track.scrollTo({
            left: el.offsetLeft - (track.clientWidth - el.offsetWidth) / 2,
            behavior: 'smooth',
        });
    };

    const onKeyDown = (e) => {
        if (!overflowing) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); scrollTo(active + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); scrollTo(active - 1); }
    };

    return (
        <div style={styles.wrap}>
            {/* Scoped scrollbar hiding — an inline style cannot express a
                pseudo-element, and a global rule would hit every scroller. */}
            <style>{'.plan-carousel-track::-webkit-scrollbar{display:none}'}</style>

            <button
                type="button"
                aria-label="Previous plan"
                style={styles.arrow('left', overflowing && active > 0)}
                onClick={() => scrollTo(active - 1)}
            >
                <Chevron dir="left" />
            </button>

            <div style={styles.viewport(overflowing ? ARROW_GUTTER : 0)}>
                <div
                    ref={trackRef}
                    className="plan-carousel-track"
                    style={styles.track(overflowing)}
                    role="group"
                    aria-label="Available plans"
                    tabIndex={overflowing ? 0 : -1}
                    onKeyDown={onKeyDown}
                >
                    {plans.map((plan, i) => (
                        <div
                            key={plan.id}
                            ref={(el) => { itemRefs.current[i] = el; }}
                            style={styles.item}
                        >
                            <PlanCard
                                plan={plan}
                                onSelect={onSelect}
                                busy={busyPlanId === plan.id}
                                ctaLabel={ctaLabel}
                                allowCurrent={allowCurrent}
                            />
                        </div>
                    ))}
                </div>
            </div>

            <button
                type="button"
                aria-label="Next plan"
                style={styles.arrow('right', overflowing && active < plans.length - 1)}
                onClick={() => scrollTo(active + 1)}
            >
                <Chevron dir="right" />
            </button>

            {overflowing && (
                <div style={styles.dots}>
                    {plans.map((plan, i) => (
                        <button
                            key={plan.id}
                            type="button"
                            aria-label={`Show ${plan.name}`}
                            aria-current={i === active}
                            style={styles.dot(i === active, accent)}
                            onClick={() => scrollTo(i)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
